import { applyDeadZone } from './stick';
import { stepDirection, type Point } from './spatialNavigation';

/**
 * Button indices from the standard mapping (https://w3c.github.io/gamepad/#dfn-standard-gamepad),
 * which names the face buttons by place, not by letter: the bottom one (A on an Xbox layout, B on
 * an 8BitDo or Nintendo one) or the right trigger picks, like a click; the right one backs out;
 * Start is the menu, like Escape; Back switches between the board, the tablets and a message. The
 * right stick turns the table; pressed in, it zooms up and down and pans left and right instead.
 * The bumpers under the index fingers zoom too, RB in and LB out (the owner's layout, 2026-09-22);
 * LT held turns the D-pad into a camera stick for a pad that has no right stick.
 */
const SELECT_BUTTONS = [0, 7];
const CANCEL_BUTTON = 1;
const MENU_BUTTON = 9;
const SWITCH_BUTTON = 8;
const CAMERA_MODIFIER = 6;
const ZOOM_IN_BUTTON = 5;
const ZOOM_OUT_BUTTON = 4;
const RIGHT_STICK_BUTTON = 11;
const DPAD = { up: 12, down: 13, left: 14, right: 15 };
const TRIGGER_PRESSED = 0.6;
/** Seconds a held direction waits before it repeats, and between repeats. */
export const REPEAT_DELAY = 0.35;
export const REPEAT_INTERVAL = 0.12;

/** What the pad asked for this frame: edges for the buttons, a step at most once per repeat. */
export interface PadFrame {
  /** Screen direction of one cursor step (y grows downwards), or none. */
  step: Point | null;
  select: boolean;
  cancel: boolean;
  menu: boolean;
  switch: boolean;
  /** Right stick, or D-pad with LT held: −1 … 1 on each axis, y up the screen positive. */
  orbit: Point;
  /** −1 (out) … 1 (in): the bumpers, or the right stick up and down while it is pressed in. */
  zoom: number;
  /** −1 (left) … 1 (right): the right stick sideways while it is pressed in. */
  pan: number;
  connected: boolean;
}

const held = (button: GamepadButton | undefined): boolean =>
  button !== undefined && (button.pressed || button.value > TRIGGER_PRESSED);

/** Rising edge of a button across every pad. */
class Edge {
  private was = false;
  take(now: boolean): boolean {
    const fired = now && !this.was;
    this.was = now;
    return fired;
  }
}

/**
 * Reads every connected controller on the board screen and turns it into one `PadFrame` a frame.
 * Polled, like the arena's pad: the Gamepad API hands out snapshots. A direction fires once when
 * pushed, then repeats while held, so a stick can be flicked cell by cell or held to run along a
 * row; releasing rearms it at once.
 */
export function createBoardPad(
  pads: () => readonly (Gamepad | null)[] = () =>
    typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function'
      ? navigator.getGamepads()
      : [],
) {
  const select = new Edge();
  const cancel = new Edge();
  const menu = new Edge();
  const switchZone = new Edge();
  let heldDirection: Point | null = null;
  let repeatAt = 0;
  let time = 0;

  return {
    /** Call once per frame with the seconds since the last call. */
    update(dt: number): PadFrame {
      time += dt;
      let connected = false;
      let selectHeld = false;
      let cancelHeld = false;
      let menuHeld = false;
      let switchHeld = false;
      let direction: Point | null = null;
      let orbit = { x: 0, z: 0 };
      let zoom = 0;
      let pan = 0;
      for (const pad of pads()) {
        if (!pad?.connected) continue;
        connected = true;
        const modifier = held(pad.buttons[CAMERA_MODIFIER]);
        const dpad = {
          x: (held(pad.buttons[DPAD.right]) ? 1 : 0) - (held(pad.buttons[DPAD.left]) ? 1 : 0),
          y: (held(pad.buttons[DPAD.down]) ? 1 : 0) - (held(pad.buttons[DPAD.up]) ? 1 : 0),
        };
        const stick = { x: pad.axes[0] ?? 0, y: pad.axes[1] ?? 0 };
        const push =
          stepDirection(stick.x, stick.y) ??
          (!modifier && (dpad.x !== 0 || dpad.y !== 0) ? stepDirection(dpad.x, dpad.y, 0) : null);
        if (push && !direction) direction = push;
        const right = applyDeadZone(pad.axes[2] ?? 0, -(pad.axes[3] ?? 0));
        // Pressed in, the right stick is a zoom and pan control rather than the camera's orbit.
        const pressedIn = held(pad.buttons[RIGHT_STICK_BUTTON]);
        if (pressedIn) {
          if (Math.abs(right.z) > Math.abs(zoom)) zoom = right.z;
          if (Math.abs(right.x) > Math.abs(pan)) pan = right.x;
        }
        const viaDpad = modifier ? { x: dpad.x, z: -dpad.y } : { x: 0, z: 0 };
        const look =
          !pressedIn && Math.hypot(right.x, right.z) >= Math.hypot(viaDpad.x, viaDpad.z)
            ? right
            : viaDpad;
        if (Math.hypot(look.x, look.z) > Math.hypot(orbit.x, orbit.z)) orbit = look;
        if (held(pad.buttons[ZOOM_IN_BUTTON])) zoom = 1;
        else if (held(pad.buttons[ZOOM_OUT_BUTTON]) && zoom === 0) zoom = -1;
        if (SELECT_BUTTONS.some((i) => held(pad.buttons[i]))) selectHeld = true;
        if (held(pad.buttons[CANCEL_BUTTON])) cancelHeld = true;
        if (held(pad.buttons[MENU_BUTTON])) menuHeld = true;
        if (held(pad.buttons[SWITCH_BUTTON])) switchHeld = true;
      }

      let step: Point | null = null;
      if (!direction) heldDirection = null;
      else if (
        !heldDirection ||
        heldDirection.x !== direction.x ||
        heldDirection.y !== direction.y
      ) {
        heldDirection = direction;
        repeatAt = time + REPEAT_DELAY;
        step = direction;
      } else if (time >= repeatAt) {
        repeatAt = time + REPEAT_INTERVAL;
        step = direction;
      }

      return {
        step,
        select: select.take(selectHeld),
        cancel: cancel.take(cancelHeld),
        menu: menu.take(menuHeld),
        switch: switchZone.take(switchHeld),
        orbit: { x: orbit.x, y: orbit.z },
        zoom,
        pan,
        connected,
      };
    },
  };
}

export type BoardPad = ReturnType<typeof createBoardPad>;
